import type { FC } from 'react';
import { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Box,
  Typography,
  TableSortLabel
} from '@mui/material';
import { reportService } from '@services/reportService';
import type { WeeklyRevenueRow } from '@/types';
import toast from 'react-hot-toast';

// Calculate initial page to show the last page
const calculateInitialPageIndex = (pageSize: number): number => {
  const startDate = new Date('2022-10-29');
  const today = new Date();
  const daysDiff = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.floor(daysDiff / 7);
  const offset = Math.max(0, totalWeeks - pageSize);
  return Math.ceil(offset / pageSize);
};

const WeeklyRevenueTable: FC = () => {
  const initialPageSize = 20;
  const [data, setData] = useState<WeeklyRevenueRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: calculateInitialPageIndex(initialPageSize),
    pageSize: initialPageSize,
  });

  useEffect(() => {
    loadData();
  }, [pagination.pageIndex, pagination.pageSize]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const limit = pagination.pageSize;
      const offset = pagination.pageIndex * limit;
      const response = await reportService.getWeeklyRevenue({ offset, limit });
      const rows = response.rows || [];
      const total = response.total || 0;
      setData(rows);
      setTotalCount(total);
    } catch (error) {
      console.error('Error loading weekly revenue:', error);
      toast.error('Failed to load weekly revenue data');
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo<ColumnDef<WeeklyRevenueRow>[]>(
    () => [
      {
        accessorKey: 'date',
        header: 'Week Starting',
        cell: (info) => {
          const dateStr = info.getValue() as string;
          // API returns "YYYY/MM/DD" format, convert to "MM/DD/YYYY"
          try {
            const [year, month, day] = dateStr.split('/');
            return `${month}/${day}/${year}`;
          } catch {
            return dateStr;
          }
        },
      },
      {
        accessorKey: 'total_hours',
        header: 'Total Hours',
        cell: (info) => (info.getValue() as number).toFixed(2),
      },
      {
        accessorKey: 'billable_hours',
        header: 'Billable Hours',
        cell: (info) => (info.getValue() as number).toFixed(2),
      },
      {
        accessorKey: 'revenue',
        header: 'Revenue',
        cell: (info) => {
          const revenueStr = info.getValue() as string;
          // API returns revenue as a string like "$0.0", parse and format properly
          try {
            const value = parseFloat(revenueStr.replace('$', ''));
            return `$${value.toFixed(2)}`;
          } catch {
            return revenueStr;
          }
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    manualPagination: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Typography textAlign="center" color="text.secondary" sx={{ mt: 3 }}>
        No weekly revenue data available
      </Typography>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id}>
                    {header.isPlaceholder ? null : (
                      <TableSortLabel
                        active={!!header.column.getIsSorted()}
                        direction={header.column.getIsSorted() === 'asc' ? 'asc' : 'desc'}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} hover>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={table.getState().pagination.pageIndex}
        onPageChange={(_event, newPage) => table.setPageIndex(newPage)}
        rowsPerPage={table.getState().pagination.pageSize}
        onRowsPerPageChange={(event) => table.setPageSize(Number(event.target.value))}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Paper>
  );
};

export default WeeklyRevenueTable;
